import { type Flags, useFlag, useRequestFeedback } from '@reflag/react-sdk';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    type ButtonKind,
    type ButtonProps,
    type ButtonSize,
    type IconDefinition,
} from '@gitbook/spine-react';

interface FeedbackButtonHookProps {
    flagKey: keyof Flags;
    feedbackDialogTitle?: string;
    openWithCommentVisible?: boolean;
}

/**
 * Hook to use the feedback button.
 */
export function useFeedbackButton(props: FeedbackButtonHookProps) {
    const {
        flagKey,
        feedbackDialogTitle = 'How do you like GitBook?',
        openWithCommentVisible = false,
    } = props;
    const feature = useFlag(flagKey);
    const requestFeedback = useRequestFeedback();

    const [isOpened, setIsOpened] = React.useState(false);

    const onPress = React.useCallback(
        (event) => {
            const target = event.target instanceof Element ? event.target : null;
            const closestButton = target?.closest('button,[role="button"]');
            const anchor =
                closestButton instanceof HTMLElement
                    ? closestButton
                    : target instanceof HTMLElement
                      ? target
                      : null;

            if (!anchor) {
                return;
            }

            requestFeedback({
                flagKey,
                title: feedbackDialogTitle,
                position: {
                    type: 'POPOVER',
                    anchor,
                },
                openWithCommentVisible,
                onClose: () => {
                    setIsOpened(false);
                },
            });
            setIsOpened(true);
        },
        [requestFeedback, flagKey, feedbackDialogTitle, openWithCommentVisible]
    );
    return {
        isEnabled: feature.isEnabled,
        buttonProps: { onPress, 'aria-expanded': isOpened },
    };
}

/**
 * A button to share feedback.
 */
export type FeedbackButtonProps = {
    kind?: ButtonKind;
    size?: ButtonSize;
    label?: string;
    icon?: IconDefinition | null;
    flagKey: keyof Flags;
    iconOnly?: ButtonProps['iconOnly'];
    feedbackDialogTitle?: string;
    openWithCommentVisible?: boolean;
};

export function FeedbackButton(props: FeedbackButtonProps) {
    const {
        kind = 'secondary',
        label = 'Share feedback',
        feedbackDialogTitle = 'How do you like GitBook?',
        icon: iconProp,
        iconOnly = false,
        size = 'small',
        flagKey,
        openWithCommentVisible,
    } = props;

    const { isEnabled, buttonProps } = useFeedbackButton({
        flagKey,
        feedbackDialogTitle,
        openWithCommentVisible,
    });

    const icon = iconProp !== null ? (iconProp ?? Icon.Happy) : null;

    if (!isEnabled) {
        return null;
    }

    return (
        <Button
            {...buttonProps}
            leadingIcon={icon ?? undefined}
            iconOnly={iconOnly}
            kind={kind}
            size={size}
            label={label}
        />
    );
}
